import { Controller } from "@hotwired/stimulus"
import TomSelect from "tom-select"

export default class extends Controller {
  static values = { 
    createUrl: String,
    placeholder: String,
    createText: String
  }

  connect() {
    this.tomSelect = new TomSelect(this.element, {
      create: (input, callback) => {
        // Handle creation directly here
        console.log('Create called with input:', input)
        this.createDocumentType(input, callback)
      },
      createOnBlur: false,  // Don't create on blur to prevent accidental creation  
      placeholder: this.placeholderValue || "Select or type to create...",
      render: {
        no_results: () => {
          return `<div class="no-results">No results found</div>`
        },
        option_create: (data, escape) => {
          const text = this.createTextValue || "Add"
          return `<div class="create">${text}: <strong>${escape(data.input)}</strong></div>`
        }
      },
      onItemAdd: (value, item) => {
        // Item added successfully
      }
    })
    
    // Add form validation to prevent submission with invalid values
    this.addFormSubmissionValidation()
  }
  
  addFormSubmissionValidation() {
    const form = this.element.closest('form')
    if (!form) return
    
    // Add multiple event listeners to catch form submission
    const validateAndPrevent = (event) => {
      const currentValue = this.tomSelect.getValue()
      console.log('Form submitting with document_type_id:', currentValue)
      
      // Check if the current value is a valid numeric ID (not text)
      const validOptions = Array.from(this.element.options).map(opt => opt.value).filter(val => val !== '')
      const isNumericId = currentValue && /^\d+$/.test(currentValue)
      const isValidOption = validOptions.includes(currentValue)
      
      // Validation check for debugging
      // console.log('Valid options:', validOptions)
      // console.log('Current value:', currentValue)
      
      if (currentValue && (!isNumericId || !isValidOption)) {
        console.log('Preventing form submission due to invalid document_type_id')
        event.preventDefault()
        event.stopImmediatePropagation()
        
        // Clear the invalid value and show message
        this.tomSelect.clear()
        this.showMessage('Please select an existing document type or create a new one by clicking the "Create" option', 'error')
        
        return false
      }
    }
    
    // Add listeners for multiple events
    form.addEventListener('submit', validateAndPrevent, true) // Capture phase
    form.addEventListener('submit', validateAndPrevent, false) // Bubble phase
    
    // Also listen on the form's submit button
    const submitButton = form.querySelector('input[type="submit"], button[type="submit"]')
    if (submitButton) {
      submitButton.addEventListener('click', validateAndPrevent)
    }
  }

  disconnect() {
    if (this.tomSelect) {
      this.tomSelect.destroy()
    }
  }

  async createDocumentType(name, callback) {
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      console.log('Creating document type:', name)
      console.log('CSRF Token:', csrfToken)
      
      // Add .json extension to force JSON format detection
      const jsonUrl = this.createUrlValue.endsWith('.json') ? this.createUrlValue : `${this.createUrlValue}.json`
      console.log('Request URL:', jsonUrl)
      
      const response = await fetch(jsonUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({
          name: name
        })
      })

      console.log('Response status:', response.status)
      console.log('Response headers:', Array.from(response.headers.entries()))
      
      let data
      const responseText = await response.text()
      console.log('Response text:', responseText)
      
      try {
        data = JSON.parse(responseText)
      } catch (parseError) {
        console.error('Failed to parse JSON:', parseError)
        throw new Error(`Server returned non-JSON response: ${responseText.substring(0, 100)}`)
      }
      
      if (response.ok) {
        console.log('Document type created successfully:', data)
        
        // Update the original select element FIRST
        const newOption = document.createElement('option')
        newOption.value = data.id.toString()
        newOption.text = data.name
        this.element.appendChild(newOption)
        console.log('Added new option to select:', data.id.toString(), data.name)
        
        // Call the callback with the proper option data
        callback({
          value: data.id.toString(),
          text: data.name
        })
        
        // Show success message if available
        if (data.message) {
          this.showMessage(data.message, 'success')
        }
      } else {
        // Handle server errors (4xx, 5xx)
        console.error('Server error:', response.status, data)
        callback()
        this.showMessage(data.error || `Server error (${response.status}): Failed to create document type`, 'error')
      }
    } catch (error) {
      console.error('Network/parsing error:', error)
      // Don't create the option on error
      callback()
      
      if (error.message.includes('non-JSON response')) {
        this.showMessage('Server returned invalid response. Please try refreshing the page.', 'error')
      } else {
        this.showMessage('Network error occurred. Please check your connection.', 'error')
      }
    }
  }

  showMessage(text, type) {
    // Create or update a message element
    let messageEl = document.querySelector('.tom-select-message')
    if (!messageEl) {
      messageEl = document.createElement('div')
      messageEl.className = 'tom-select-message'
      this.element.parentNode.insertBefore(messageEl, this.element.nextSibling)
    }
    
    messageEl.className = `tom-select-message alert alert-${type === 'error' ? 'danger' : 'success'}`
    messageEl.textContent = text
    
    // Auto-hide after 3 seconds
    setTimeout(() => {
      if (messageEl.parentNode) {
        messageEl.remove()
      }
    }, 3000)
  }
}